import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { CheckCircleIcon, PaperAirplaneIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline'

interface LessonData {
  date: string
  sessionTime: string
  studentGroups: string[]
  subjects: {
    math: {
      enabled: boolean
      topic: string
      content: string
      homework: string
    }
    thai: {
      enabled: boolean
      topic: string
      content: string
      homework: string
    }
    english: {
      enabled: boolean
      topic: string
      content: string
      homework: string
    }
  }
  images: File[]
  notifyParents: boolean
}

export function AdminLessonPostPage() {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [lessonData, setLessonData] = useState<LessonData>({
    date: new Date().toISOString().split('T')[0],
    sessionTime: '09:00-15:00',
    studentGroups: ['all'],
    subjects: {
      math: {
        enabled: true,
        topic: '',
        content: '',
        homework: ''
      },
      thai: {
        enabled: true,
        topic: '',
        content: '',
        homework: ''
      },
      english: {
        enabled: true,
        topic: '',
        content: '',
        homework: ''
      }
    },
    images: [],
    notifyParents: true
  })

  const [activeTab, setActiveTab] = useState<'math' | 'thai' | 'english'>('math')

  const handleSubjectChange = (subject: 'math' | 'thai' | 'english', field: string, value: string) => {
    setLessonData(prev => ({
      ...prev,
      subjects: {
        ...prev.subjects,
        [subject]: {
          ...prev.subjects[subject],
          [field]: value
        }
      }
    }))
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newImages = Array.from(e.target.files)
      setLessonData(prev => ({
        ...prev,
        images: [...prev.images, ...newImages]
      }))
    }
  }

  const removeImage = (index: number) => {
    setLessonData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }))
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    // Navigate to success page instead of showing alert
    const enabledSubjects = Object.entries(lessonData.subjects)
      .filter(([_, subject]) => subject.enabled && subject.topic)
      .map(([key, _]) => {
        const labels = { math: 'คณิตศาสตร์', thai: 'ภาษาไทย', english: 'ภาษาอังกฤษ' }
        return labels[key as keyof typeof labels]
      })

    navigate('/admin/lessons/success', {
      state: {
        date: lessonData.date,
        subjectsCount: enabledSubjects.length,
        studentsNotified: 18, // Mock student count
        subjects: enabledSubjects,
        hasImages: lessonData.images.length > 0
      },
      replace: true
    })
  }

  const useTemplate = () => {
    setLessonData(prev => ({
      ...prev,
      subjects: {
        math: {
          enabled: true,
          topic: 'การบวกลบเลข 3 หลัก',
          content: '- ทบทวนการบวกตัวเลข 3 หลัก\n- การทดในหลักสิบ\n- แบบฝึกหัด 10 ข้อ',
          homework: 'แบบฝึกหัดหน้า 25 ข้อ 1-10'
        },
        thai: {
          enabled: true,
          topic: 'การผันวรรณยุกต์',
          content: '- วรรณยุกต์ 5 เสียง\n- ฝึกผันคำ\n- อ่านเรื่อง "บ้านของเรา"',
          homework: 'อ่านเรื่อง "บ้านของเรา" และตอบคำถามท้ายเรื่อง'
        },
        english: {
          enabled: true,
          topic: 'Present Simple Tense',
          content: '- I, You, We, They + V1\n- He, She, It + Vs/es\n- Vocabulary: Family members',
          homework: 'Workbook page 15-16'
        }
      }
    }))
  }

  const subjectConfig = {
    math: { 
      label: 'คณิตศาสตร์', 
      icon: '🔢', 
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-900'
    },
    thai: { 
      label: 'ภาษาไทย', 
      icon: '🇹🇭', 
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-900'
    },
    english: { 
      label: 'ภาษาอังกฤษ', 
      icon: '🔤', 
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-900'
    }
  }

  if (showPreview) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader 
          title="ตรวจสอบก่อนส่ง"
          showBackButton={true}
          onBack={() => setShowPreview(false)}
        />
        
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">📅 วันที่ {lessonData.date}</h3>
              <p className="text-sm text-gray-600">เวลา: {lessonData.sessionTime}</p>
              <p className="text-sm text-gray-600">ส่งถึง: ผู้ปกครองทั้งหมด (18 คน)</p>
            </div>

            <div className="space-y-4">
              {lessonData.subjects.math.enabled && lessonData.subjects.math.topic && (
                <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <h4 className="font-medium text-blue-900 mb-2">🔢 คณิตศาสตร์</h4>
                  <p className="text-sm font-medium text-blue-800">หัวข้อ: {lessonData.subjects.math.topic}</p>
                  <p className="text-sm text-blue-700 whitespace-pre-line mt-1">{lessonData.subjects.math.content}</p>
                  {lessonData.subjects.math.homework && (
                    <p className="text-sm text-blue-600 mt-2">📝 การบ้าน: {lessonData.subjects.math.homework}</p>
                  )}
                </div>
              )}

              {lessonData.subjects.thai.enabled && lessonData.subjects.thai.topic && (
                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                  <h4 className="font-medium text-green-900 mb-2">🇹🇭 ภาษาไทย</h4>
                  <p className="text-sm font-medium text-green-800">หัวข้อ: {lessonData.subjects.thai.topic}</p>
                  <p className="text-sm text-green-700 whitespace-pre-line mt-1">{lessonData.subjects.thai.content}</p>
                  {lessonData.subjects.thai.homework && (
                    <p className="text-sm text-green-600 mt-2">📝 การบ้าน: {lessonData.subjects.thai.homework}</p>
                  )}
                </div>
              )}

              {lessonData.subjects.english.enabled && lessonData.subjects.english.topic && (
                <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                  <h4 className="font-medium text-purple-900 mb-2">🔤 ภาษาอังกฤษ</h4>
                  <p className="text-sm font-medium text-purple-800">หัวข้อ: {lessonData.subjects.english.topic}</p>
                  <p className="text-sm text-purple-700 whitespace-pre-line mt-1">{lessonData.subjects.english.content}</p>
                  {lessonData.subjects.english.homework && (
                    <p className="text-sm text-purple-600 mt-2">📝 การบ้าน: {lessonData.subjects.english.homework}</p>
                  )}
                </div>
              )}
            </div>

            {lessonData.images.length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-medium text-gray-700 mb-2">📷 รูปภาพแนบ: {lessonData.images.length} รูป</p>
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  {lessonData.notifyParents ? '✅ จะส่งการแจ้งเตือนผ่าน LINE' : '❌ ไม่ส่งการแจ้งเตือน'}
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowPreview(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  ← แก้ไข
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <LoadingSpinner size="small" />
                  ) : (
                    <>
                      <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                      ส่งบทเรียน
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="โพสต์บทเรียน"
        showBackButton={true}
      />
      
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Session Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <h3 className="font-medium text-gray-900 mb-3">ข้อมูลรอบเรียน</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">วันที่</label>
              <input
                type="date"
                value={lessonData.date}
                onChange={(e) => setLessonData(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">เวลาเรียน</label>
              <select
                value={lessonData.sessionTime}
                onChange={(e) => setLessonData(prev => ({ ...prev, sessionTime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="09:00-15:00">เสาร์ 09:00-15:00</option>
                <option value="09:00-12:00">เสาร์เช้า 09:00-12:00</option>
                <option value="13:00-16:00">เสาร์บ่าย 13:00-16:00</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={useTemplate}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              💡 ใช้เทมเพลตสัปดาห์ที่แล้ว
            </button>
          </div>
        </div>

        {/* Subject Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="flex border-b border-gray-200">
            {(['math', 'thai', 'english'] as const).map((subject) => (
              <button
                key={subject}
                onClick={() => setActiveTab(subject)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === subject
                    ? `${subjectConfig[subject].bgColor} ${subjectConfig[subject].textColor} border-b-2 border-current`
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {subjectConfig[subject].icon} {subjectConfig[subject].label}
                {lessonData.subjects[subject].topic && (
                  <CheckCircleIcon className="h-4 w-4 ml-2 inline" />
                )}
              </button>
            ))}
          </div>

          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    checked={lessonData.subjects[activeTab].enabled}
                    onChange={(e) => handleSubjectChange(activeTab, 'enabled', e.target.checked ? 'true' : '')}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    สอนวิชานี้วันนี้
                  </span>
                </label>
              </div>

              {lessonData.subjects[activeTab].enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      หัวข้อหลัก <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={lessonData.subjects[activeTab].topic}
                      onChange={(e) => handleSubjectChange(activeTab, 'topic', e.target.value)}
                      placeholder={`เช่น ${activeTab === 'math' ? 'การบวกลบเลข 3 หลัก' : activeTab === 'thai' ? 'การผันวรรณยุกต์' : 'Present Simple Tense'}`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      เนื้อหาที่สอน <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={lessonData.subjects[activeTab].content}
                      onChange={(e) => handleSubjectChange(activeTab, 'content', e.target.value)}
                      placeholder="รายละเอียดเนื้อหาที่สอนวันนี้..."
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      การบ้าน
                    </label>
                    <input
                      type="text"
                      value={lessonData.subjects[activeTab].homework}
                      onChange={(e) => handleSubjectChange(activeTab, 'homework', e.target.value)}
                      placeholder="การบ้านที่มอบหมาย (ถ้ามี)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Image Upload */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <h3 className="font-medium text-gray-900 mb-3">📷 รูปภาพจากชั้นเรียน</h3>
          
          <div className="space-y-3">
            {lessonData.images.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {lessonData.images.map((image, index) => (
                  <div key={index} className="relative group">
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                      <img 
                        src={URL.createObjectURL(image)} 
                        alt={`รูปที่ ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <label className="block">
              <div className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 cursor-pointer transition-colors">
                <PhotoIcon className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600">เพิ่มรูปภาพ</span>
              </div>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={lessonData.notifyParents}
              onChange={(e) => setLessonData(prev => ({ ...prev, notifyParents: e.target.checked }))}
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm font-medium text-gray-700">
              ส่งการแจ้งเตือนไปยังผู้ปกครองผ่าน LINE
            </span>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/admin/lessons')}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            ยกเลิก
          </button>
          <button
            onClick={() => setShowPreview(true)}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            ตรวจสอบ →
          </button>
        </div>
      </div>
    </div>
  )
}